import React, { useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalCloseButton,
  Box, VStack, Heading, Text, FormControl, Input,
  InputGroup, InputLeftElement, Button, HStack, Divider,
  Alert, AlertIcon, Link, useToast, Flex, Icon,
  PinInput, PinInputField, useDisclosure,
} from "@chakra-ui/react";
import { FiMail, FiLock, FiUser, FiArrowRight, FiShield, FiArrowLeft, FiKey, FiCheck, FiAtSign } from 'react-icons/fi';
import axios from 'axios';
import GoogleSignIn from './GoogleSignIn';
import { getSafeImageUrl } from '../../utils/imageUtils';
import { notifyAuthChanged } from '../../utils/authEvents';
import { useLang } from '../../contexts/LangContext';

const RED      = "#a21b1b";
const RED_DARK = "#881616";

export default function Modallogin({ staticModal, setStaticModal }) {
  const [tabIndex,    setTabIndex]   = useState(0); // 0=login 1=register
  const isLogin = tabIndex === 0;
  const [err,       setErr]       = useState();
  const toast      = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const { t }      = useLang();

  const handleClose = () => setStaticModal(false);

  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [loginPassword,   setLoginPassword]   = useState('');

  const [regName,      setRegName]      = useState('');
  const [regEmail,     setRegEmail]     = useState('');
  const [regUsername,  setRegUsername]  = useState('');
  const [regPassword,  setRegPassword]  = useState('');
  const [regRPassword, setRegRPassword] = useState('');
  const [registered,   setRegistered]   = useState(false);
  const [regError,     setRegError]     = useState('');
  const [isLoading,    setIsLoading]    = useState(false);

  const { isOpen: isForgotOpen, onOpen: onForgotOpen, onClose: onForgotClose } = useDisclosure();
  const [forgotStep,         setForgotStep]         = useState(1);
  const [forgotEmail,        setForgotEmail]        = useState('');
  const [forgotOTP,          setForgotOTP]          = useState('');
  const [newPassword,        setNewPassword]        = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [forgotLoading,      setForgotLoading]      = useState(false);
  const [forgotError,        setForgotError]        = useState('');

  const inputStyle = {
    size: "md", bg: "gray.50", border: "1.5px solid", borderColor: "gray.200", borderRadius: "xl",
    fontSize: "16px", h: "48px",
    _hover: { borderColor: "gray.300", bg: "white" },
    _focus: { borderColor: RED, bg: "white", boxShadow: `0 0 0 3px rgba(162,27,27,0.08)` },
  };

  const switchTab = (i) => { setTabIndex(i); setErr(null); setRegError(''); };

  const handleForgotOpen = () => {
    setForgotStep(1); setForgotEmail(''); setForgotOTP('');
    setNewPassword(''); setConfirmNewPassword(''); setForgotError('');
    onForgotOpen();
  };

  const sendResetOTP = async () => {
    if (!forgotEmail) { setForgotError(t('err_email_required') || 'Email requis'); return; }
    setForgotLoading(true); setForgotError('');
    try {
      await axios.post(`${backendUrl}/forgot-password`, { email: forgotEmail });
      setForgotStep(2);
      toast({ title: t('otp_sent'), description: t('otp_sent_desc') + ' ' + forgotEmail, status: 'success', duration: 5000, isClosable: true });
    } catch (e) {
      setForgotError(e.response?.data?.message || t('error'));
    } finally { setForgotLoading(false); }
  };

  const verifyAndResetPassword = async () => {
    if (forgotOTP.length !== 4) { setForgotError(t('otp_invalid')); return; }
    if (newPassword.length < 6) { setForgotError(t('password_too_short')); return; }
    if (newPassword !== confirmNewPassword) { setForgotError(t('passwords_no_match')); return; }
    setForgotLoading(true); setForgotError('');
    try {
      await axios.post(`${backendUrl}/reset-password`, { email: forgotEmail, otp: forgotOTP, newPassword });
      setForgotStep(3);
    } catch (e) {
      setForgotError(e.response?.data?.message || t('error'));
    } finally { setForgotLoading(false); }
  };

  const toSlugUsername = (str) => {
    let slug = str.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 26) || 'user';
    while (slug.length < 4) slug += Math.floor(Math.random() * 10);
    return slug;
  };

  function register(e) {
    e.preventDefault();
    if (regPassword !== regRPassword) { setRegError(t('passwords_no_match')); return; }
    const finalUsername = regUsername.trim() || toSlugUsername(regName || regEmail.split('@')[0]);
    setIsLoading(true);
    axios.post(`${backendUrl}/register`, { email: regEmail, password: regPassword, name: regName, username: finalUsername })
      .then(() => { setRegistered(true); setIsLoading(false); toast({ title: t('account_created'), status: 'success', duration: 5000, isClosable: true }); })
      .catch((err) => {
        setIsLoading(false); setErr(err.response?.status);
        setRegError(err.response?.data?.message || t('error'));
      });
  }

  function login(e) {
    e.preventDefault();
    setIsLoading(true);
    axios.post(`${backendUrl}/login`, { identifier: loginIdentifier.trim(), password: loginPassword })
      .then((r) => {
        localStorage.setItem('authToken',    r.data.token);
        localStorage.setItem('authemail',   r.data.email);
        localStorage.setItem('authname',    r.data.name);
        localStorage.setItem('authusername',r.data.username || '');
        localStorage.setItem('authphone',   r.data.phone || '');
        localStorage.setItem('authpicture', getSafeImageUrl(r.data.picture, 96));
        notifyAuthChanged();
        window.location.href = '/';
      })
      .catch((err) => { setIsLoading(false); setErr(err.response?.status); });
  }

  const pinStyle = {
    borderRadius: "xl", border: "2px solid", borderColor: "gray.200",
    _focus: { borderColor: RED, boxShadow: `0 0 0 3px rgba(162,27,27,0.08)` },
    fontSize: "xl", fontWeight: "bold", w: "3rem", h: "3rem",
  };

  return (
    <Modal
      isOpen={staticModal}
      onClose={handleClose}
      isCentered
      size={{ base: "full", sm: "md" }}
      motionPreset="slideInBottom"
      scrollBehavior="inside"
      closeOnOverlayClick
      closeOnEsc
    >
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
      <ModalContent
        borderRadius={{ base: "28px 28px 0 0", sm: "2xl" }}
        overflow="hidden" bg="white"
        boxShadow="0 25px 60px -12px rgba(0,0,0,0.4)"
        mx={{ base: 0, sm: 4 }}
        maxH={{ base: "95vh", sm: "90vh" }}
        mt={{ base: "auto", sm: "auto" }}
        mb={{ base: 0, sm: "auto" }}
      >
        {/* ── Brand Header ── */}
        <Box
          bgGradient={`linear(135deg, ${RED} 0%, #c0392b 100%)`}
          pt={{ base: 7, sm: 8 }} pb={6} px={6}
          position="relative" overflow="hidden"
        >
          <Box position="absolute" top="-60px" right="-40px" w="160px" h="160px" borderRadius="full" bg="rgba(255,255,255,0.07)" />
          <Box position="absolute" bottom="-40px" left="-30px" w="100px" h="100px" borderRadius="full" bg="rgba(255,255,255,0.05)" />

          <ModalCloseButton
            color="white" top={3} right={3}
            borderRadius="full" w={9} h={9}
            bg="rgba(255,255,255,0.18)"
            _hover={{ bg: 'rgba(255,255,255,0.3)' }}
            zIndex={10}
          />

          <VStack spacing={1.5} align="center" position="relative" zIndex={1} mb={5}>
            <Heading size="lg" color="white" fontWeight="800" letterSpacing="-0.5px">
              9achna
            </Heading>
            <Text color="whiteAlpha.800" fontSize="xs" fontWeight="500">
              {isLogin ? t('login_subtitle') : t('register_subtitle')}
            </Text>
          </VStack>

          {/* Tab switcher inside header */}
          <Box
            bg="rgba(0,0,0,0.2)"
            borderRadius="xl"
            p={1}
            mx={2}
            position="relative" zIndex={1}
          >
            <Flex>
              {[t('sign_in'), t('create_account')].map((label, i) => (
                <Box
                  key={i}
                  flex={1}
                  py={2}
                  textAlign="center"
                  borderRadius="lg"
                  cursor="pointer"
                  bg={tabIndex === i ? "white" : "transparent"}
                  color={tabIndex === i ? RED : "whiteAlpha.800"}
                  fontWeight={tabIndex === i ? "700" : "500"}
                  fontSize="sm"
                  transition="all 0.2s"
                  onClick={() => switchTab(i)}
                  boxShadow={tabIndex === i ? "sm" : "none"}
                  userSelect="none"
                >
                  {label}
                </Box>
              ))}
            </Flex>
          </Box>
        </Box>

        {/* ── Body ── */}
        <Box px={{ base: 4, sm: 6 }} py={5} overflowY="auto" pb={{ base: 8, sm: 6 }}>
          {/* Google SSO */}
          <VStack spacing={3} mb={4}>
            <Box w="full" display="flex" justifyContent="center">
              <GoogleSignIn isVisible={staticModal} />
            </Box>
            <HStack w="full" spacing={3}>
              <Divider borderColor="gray.200" />
              <Text fontSize="xs" color="gray.400" whiteSpace="nowrap" fontWeight="600" textTransform="uppercase" px={1}>{t('or_email')}</Text>
              <Divider borderColor="gray.200" />
            </HStack>
          </VStack>

          {/* ── Login ── */}
          {isLogin ? (
            <form onSubmit={login}>
              <VStack spacing={3}>
                <FormControl isRequired>
                  <Text fontSize="sm" fontWeight="600" color="gray.600" mb={1.5}>{t('identifier_label')}</Text>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none" h="48px"><Icon as={FiAtSign} color="gray.400" boxSize={4} /></InputLeftElement>
                    <Input type="text" value={loginIdentifier} onChange={(e) => setLoginIdentifier(e.target.value)} placeholder={t('identifier_placeholder')} {...inputStyle} pl={9} />
                  </InputGroup>
                </FormControl>
                <FormControl isRequired>
                  <Flex justify="space-between" align="center" mb={1.5}>
                    <Text fontSize="sm" fontWeight="600" color="gray.600">{t('password_label')}</Text>
                    <Link color={RED} fontSize="xs" fontWeight="600" onClick={handleForgotOpen} cursor="pointer" _hover={{ color: RED_DARK }}>{t('forgot_password')}</Link>
                  </Flex>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none" h="48px"><Icon as={FiLock} color="gray.400" boxSize={4} /></InputLeftElement>
                    <Input type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} placeholder="••••••••" {...inputStyle} pl={9} />
                  </InputGroup>
                </FormControl>
                {err === 404 && <Alert status="error" borderRadius="xl" fontSize="sm" py={2}><AlertIcon />{t('email_not_found')}</Alert>}
                {err === 400 && <Alert status="error" borderRadius="xl" fontSize="sm" py={2}><AlertIcon />{t('incorrect_password')}</Alert>}
                <Button
                  type="submit" w="full" h="52px"
                  bgGradient={`linear(135deg, ${RED} 0%, #c0392b 100%)`}
                  color="white" borderRadius="xl" fontWeight="700" fontSize="md"
                  rightIcon={<FiArrowRight />} isLoading={isLoading} loadingText={t('signing_in')}
                  _hover={{ transform: 'translateY(-2px)', boxShadow: `0 12px 28px rgba(162,27,27,0.4)` }}
                  _active={{ transform: 'translateY(0)' }} transition="all 0.2s"
                  mt={1}
                >
                  {t('sign_in_btn')}
                </Button>
              </VStack>
            </form>

          /* ── Register success ── */
          ) : registered ? (
            <VStack spacing={4} py={4} textAlign="center">
              <Box w={16} h={16} mx="auto" bgGradient="linear(135deg, green.400, green.500)" borderRadius="full"
                display="flex" alignItems="center" justifyContent="center" boxShadow="0 8px 20px rgba(72,187,120,0.35)">
                <Icon as={FiCheck} w={8} h={8} color="white" />
              </Box>
              <Heading size="sm" color="gray.800">{t('account_created')}</Heading>
              <Text color="gray.500" fontSize="sm">{t('account_created_desc')}</Text>
              <Button
                bgGradient={`linear(135deg, ${RED} 0%, #c0392b 100%)`}
                color="white" borderRadius="xl" h="48px" w="full"
                onClick={() => { setRegistered(false); switchTab(0); }}
                rightIcon={<FiArrowRight />}
                _hover={{ transform: 'translateY(-1px)', boxShadow: `0 8px 20px rgba(162,27,27,0.35)` }}
              >
                {t('go_to_login')}
              </Button>
            </VStack>

          /* ── Register form ── */
          ) : (
            <form onSubmit={register}>
              <VStack spacing={3}>
                <FormControl isRequired>
                  <Text fontSize="sm" fontWeight="600" color="gray.600" mb={1.5}>{t('full_name')}</Text>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none" h="48px"><Icon as={FiUser} color="gray.400" boxSize={4} /></InputLeftElement>
                    <Input type="text" value={regName}
                      onChange={(e) => setRegName(e.target.value)}
                      onBlur={(e) => { if (!regUsername && e.target.value.trim().length >= 2) setRegUsername(toSlugUsername(e.target.value.trim())); }}
                      placeholder={t('full_name')} {...inputStyle} pl={9} />
                  </InputGroup>
                </FormControl>
                <FormControl>
                  <Text fontSize="sm" fontWeight="600" color="gray.600" mb={1.5}>{t('username_label')}</Text>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none" h="48px"><Icon as={FiAtSign} color="gray.400" boxSize={4} /></InputLeftElement>
                    <Input type="text" value={regUsername} onChange={(e) => setRegUsername(e.target.value)} placeholder={t('username_placeholder')} {...inputStyle} pl={9} />
                  </InputGroup>
                </FormControl>
                <FormControl isRequired>
                  <Text fontSize="sm" fontWeight="600" color="gray.600" mb={1.5}>{t('email_label')}</Text>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none" h="48px"><Icon as={FiMail} color="gray.400" boxSize={4} /></InputLeftElement>
                    <Input type="email" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} placeholder="exemple@email.com" {...inputStyle} pl={9} />
                  </InputGroup>
                </FormControl>
                <HStack spacing={3} w="full">
                  <FormControl isRequired>
                    <Text fontSize="sm" fontWeight="600" color="gray.600" mb={1.5}>{t('password_label')}</Text>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none" h="48px"><Icon as={FiLock} color="gray.400" boxSize={4} /></InputLeftElement>
                      <Input type="password" value={regPassword} onChange={(e) => setRegPassword(e.target.value)} placeholder="••••••••" {...inputStyle} pl={9} />
                    </InputGroup>
                  </FormControl>
                  <FormControl isRequired>
                    <Text fontSize="sm" fontWeight="600" color="gray.600" mb={1.5}>{t('confirm_password')}</Text>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none" h="48px"><Icon as={FiShield} color="gray.400" boxSize={4} /></InputLeftElement>
                      <Input type="password" value={regRPassword} onChange={(e) => setRegRPassword(e.target.value)} placeholder="••••••••" {...inputStyle} pl={9} />
                    </InputGroup>
                  </FormControl>
                </HStack>
                {regError && <Alert status="error" borderRadius="xl" fontSize="sm" py={2}><AlertIcon />{regError}</Alert>}
                {err === 409 && <Alert status="error" borderRadius="xl" fontSize="sm" py={2}><AlertIcon />{t('user_exists')}</Alert>}
                <Button
                  type="submit" w="full" h="52px"
                  bgGradient={`linear(135deg, ${RED} 0%, #c0392b 100%)`}
                  color="white" borderRadius="xl" fontWeight="700" fontSize="md"
                  rightIcon={<FiArrowRight />} isLoading={isLoading} loadingText={t('creating_account_loading')}
                  _hover={{ transform: 'translateY(-2px)', boxShadow: `0 12px 28px rgba(162,27,27,0.4)` }}
                  _active={{ transform: 'translateY(0)' }} transition="all 0.2s"
                  mt={1}
                >
                  {t('create_account')}
                </Button>
              </VStack>
            </form>
          )}
        </Box>
      </ModalContent>

      {/* ── Forgot Password Modal ── */}
      <Modal isOpen={isForgotOpen} onClose={onForgotClose} isCentered size="sm">
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
        <ModalContent borderRadius="2xl" mx={4} overflow="hidden">
          <ModalCloseButton color="white" top={3} right={3} borderRadius="full" bg="rgba(255,255,255,0.18)" _hover={{ bg: 'rgba(255,255,255,0.3)' }} />
          <Box bgGradient={`linear(135deg, ${RED} 0%, #c0392b 100%)`} py={6} px={6} textAlign="center">
            <VStack spacing={2}>
              <Box w={11} h={11} borderRadius="xl" bg="rgba(255,255,255,0.2)" display="flex" alignItems="center" justifyContent="center" mx="auto">
                <Icon as={forgotStep === 3 ? FiCheck : FiKey} boxSize={5} color="white" />
              </Box>
              <Text color="white" fontWeight="700" fontSize="md">
                {forgotStep === 1 && t('forgot_password_title')}
                {forgotStep === 2 && t('enter_otp')}
                {forgotStep === 3 && t('password_reset_success')}
              </Text>
            </VStack>
          </Box>
          <Box py={6} px={6}>
            {forgotStep === 1 && (
              <VStack spacing={4}>
                <Text color="gray.500" textAlign="center" fontSize="sm">{t('forgot_password_hint')}</Text>
                <FormControl>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none" h="48px"><Icon as={FiMail} color="gray.400" /></InputLeftElement>
                    <Input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="exemple@email.com" {...inputStyle} pl={9} />
                  </InputGroup>
                </FormControl>
                {forgotError && <Alert status="error" borderRadius="xl" fontSize="sm" py={2}><AlertIcon />{forgotError}</Alert>}
                <Button w="full" h="48px" bgGradient={`linear(135deg, ${RED} 0%, #c0392b 100%)`} color="white" borderRadius="xl" fontWeight="700"
                  onClick={sendResetOTP} isLoading={forgotLoading} rightIcon={<FiArrowRight />} _hover={{ transform: 'translateY(-1px)' }}>
                  {t('send_otp_btn')}
                </Button>
              </VStack>
            )}
            {forgotStep === 2 && (
              <VStack spacing={4}>
                <Text color="gray.500" textAlign="center" fontSize="sm">{t('otp_sent_desc')} <strong>{forgotEmail}</strong></Text>
                <HStack justify="center" spacing={3}>
                  <PinInput otp size="lg" value={forgotOTP} onChange={setForgotOTP}>
                    {[0,1,2,3].map((i) => <PinInputField key={i} {...pinStyle} />)}
                  </PinInput>
                </HStack>
                <FormControl>
                  <Text fontWeight="600" color="gray.700" fontSize="sm" mb={1.5}>{t('new_password')}</Text>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none" h="48px"><Icon as={FiLock} color="gray.400" /></InputLeftElement>
                    <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="••••••••" {...inputStyle} pl={9} />
                  </InputGroup>
                </FormControl>
                <FormControl>
                  <Text fontWeight="600" color="gray.700" fontSize="sm" mb={1.5}>{t('confirm_password')}</Text>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none" h="48px"><Icon as={FiShield} color="gray.400" /></InputLeftElement>
                    <Input type="password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} placeholder="••••••••" {...inputStyle} pl={9} />
                  </InputGroup>
                </FormControl>
                {forgotError && <Alert status="error" borderRadius="xl" fontSize="sm" py={2}><AlertIcon />{forgotError}</Alert>}
                <HStack w="full" spacing={3}>
                  <Button flex={1} h="48px" variant="outline" borderRadius="xl" leftIcon={<FiArrowLeft />} onClick={() => setForgotStep(1)} borderColor="gray.200">{t('back')}</Button>
                  <Button flex={2} h="48px" bgGradient={`linear(135deg, ${RED} 0%, #c0392b 100%)`} color="white" borderRadius="xl" fontWeight="700"
                    onClick={verifyAndResetPassword} isLoading={forgotLoading} rightIcon={<FiArrowRight />} _hover={{ transform: 'translateY(-1px)' }}>
                    {t('reset_password_btn')}
                  </Button>
                </HStack>
                <Button variant="link" color={RED} onClick={sendResetOTP} isLoading={forgotLoading} fontSize="sm">{t('resend_otp')}</Button>
              </VStack>
            )}
            {forgotStep === 3 && (
              <VStack spacing={4} textAlign="center">
                <Box w={20} h={20} mx="auto" bgGradient="linear(135deg, green.400, green.500)" borderRadius="full"
                  display="flex" alignItems="center" justifyContent="center" boxShadow="0 8px 20px rgba(72,187,120,0.35)">
                  <Icon as={FiCheck} color="white" boxSize={10} />
                </Box>
                <Text color="gray.700" fontWeight="700" fontSize="lg">{t('password_reset_success')}</Text>
                <Text color="gray.400" fontSize="sm">{t('now_sign_in')}</Text>
                <Button w="full" h="48px" bgGradient={`linear(135deg, ${RED} 0%, #c0392b 100%)`} color="white" borderRadius="xl" fontWeight="700"
                  onClick={onForgotClose} _hover={{ transform: 'translateY(-1px)' }}>
                  {t('back_to_login')}
                </Button>
              </VStack>
            )}
          </Box>
        </ModalContent>
      </Modal>
    </Modal>
  );
}
